import React, { useEffect, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getFirestore, collection, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../firebase-config";
import { useAuth } from "../auth-context";

const OrderDashboard = () => {
  const { userRole } = useAuth();
  const isChef = userRole === 'chef';

  const theme = React.useMemo(() => createTheme({
    palette: {
      primary: {
        main: "#2E7D32", // Authentic Crispy Dosa Green
      },
      secondary: {
        main: "#FF6F00", // Orange Accent
      },
    },
  }), [isChef]);

  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [items, setItems] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  const [scrollPosition, setScrollPosition] = useState(0);
  const [inventoryItems, setInventoryItems] = useState([]);

  // Date filters
  const [dateFilter, setDateFilter] = useState("Today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const inventoryItemsMap = React.useMemo(() => {
    return inventoryItems.reduce((map, invItem) => {
      map[invItem.id] = invItem;
      return map;
    }, {});
  }, [inventoryItems]);

  // UK Time helper functions
  const getUKTime = (date = new Date()) => {
    return new Date(date.toLocaleString("en-US", { timeZone: "Europe/London" }));
  };

  const getDateRange = (filterType, startStr, endStr) => {
    const now = new Date();
    const ukNow = getUKTime(now);

    // Today's business start/end (9am - 9am UK time)
    const todayStart = new Date(ukNow);
    if (ukNow.getHours() < 9) {
      todayStart.setDate(ukNow.getDate() - 1);
    }
    todayStart.setHours(9, 0, 0, 0);

    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);
    todayEnd.setHours(9, 0, 0, 0);

    switch (filterType) {
      case "Today":
        return { start: todayStart, end: todayEnd };
      case "Yesterday": {
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        const yesterdayEnd = new Date(todayStart);
        return { start: yesterdayStart, end: yesterdayEnd };
      }
      case "Last 3 Days": {
        const threeDaysStart = new Date(todayStart);
        threeDaysStart.setDate(threeDaysStart.getDate() - 2); // 3 days including today
        return { start: threeDaysStart, end: todayEnd };
      }
      case "Custom": {
        if (!startStr || !endStr) {
          return { start: todayStart, end: todayEnd };
        }
        const [sYear, sMonth, sDay] = startStr.split('-').map(Number);
        const start = new Date(sYear, sMonth - 1, sDay, 9, 0, 0, 0);

        const [eYear, eMonth, eDay] = endStr.split('-').map(Number);
        const end = new Date(eYear, eMonth - 1, eDay + 1, 9, 0, 0, 0);

        return { start, end };
      }
      default:
        return { start: todayStart, end: todayEnd };
    }
  };

  const getRangeText = () => {
    const { start, end } = getDateRange(dateFilter, customStart, customEnd);
    const options = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return `📍 UK Time Range: ${start.toLocaleString("en-GB", options)} to ${end.toLocaleString("en-GB", options)}`;
  };

  // Fetch inventory items on mount
  useEffect(() => {
    const fetchInventoryItems = async () => {
      try {
        const dbInstance = getFirestore();
        const inventorySnapshot = await getDocs(collection(dbInstance, "inventoryItems"));
        const inventoryData = [];
        inventorySnapshot.forEach(doc => {
          inventoryData.push({ id: doc.id, ...doc.data() });
        });
        setInventoryItems(inventoryData);
      } catch (err) {
        console.error("Error fetching inventory items:", err);
      }
    };

    fetchInventoryItems();
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const dbInstance = getFirestore();
        const categoriesSnapshot = await getDocs(collection(dbInstance, "inventoryCategory"));
        const categoriesData = {};

        categoriesSnapshot.forEach((doc) => {
          categoriesData[doc.id] = doc.data().category;
        });

        setCategories(categoriesData);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };

    fetchCategories();
  }, []);

  // Listen to invoices and fetch all orders
  useEffect(() => {
    setLoading(true);
    const invoicesRef = collection(db, "invoices");
    const unsubscribe = onSnapshot(invoicesRef, (snapshot) => {
      const invoicesList = [];
      snapshot.forEach((doc) => {
        invoicesList.push({ id: doc.id, ...doc.data() });
      });
      setInvoices(invoicesList);
      setLoading(false);
    }, (err) => {
      console.error("Error subscribing to invoices:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Apply filters to invoices
  useEffect(() => {
    if (invoices.length === 0) {
      setFilteredInvoices([]);
      return;
    }

    const { start, end } = getDateRange(dateFilter, customStart, customEnd);

    const filtered = invoices.filter((invoice) => {
      const createdAt = invoice.createdAt;
      let invoiceDate;

      if (createdAt && createdAt.seconds) {
        invoiceDate = new Date(createdAt.seconds * 1000);
      } else if (typeof createdAt === "string") {
        invoiceDate = new Date(createdAt);
      } else {
        return false;
      }

      return invoiceDate >= start && invoiceDate < end;
    });

    setFilteredInvoices(filtered);
  }, [invoices, dateFilter, customStart, customEnd]);

  // Process filtered invoices
  useEffect(() => {
    if (filteredInvoices.length >= 0 && Object.keys(categories).length > 0) {
      processInvoices();
    }
  }, [filteredInvoices, categories, inventoryItems]);

  // Adjust scroll position of category headers during horizontal table scroll
  useEffect(() => {
    const categoryHeaders = document.querySelectorAll('.category-label');
    categoryHeaders.forEach(header => {
      if (header) {
        header.style.transform = `translateX(${scrollPosition}px)`;
      }
    });
  }, [scrollPosition]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const dbInstance = getFirestore();
      
      const categoriesSnapshot = await getDocs(collection(dbInstance, "inventoryCategory"));
      const categoriesData = {};
      categoriesSnapshot.forEach((doc) => {
        categoriesData[doc.id] = doc.data().category;
      });
      setCategories(categoriesData);

      const inventorySnapshot = await getDocs(collection(dbInstance, "inventoryItems"));
      const inventoryData = [];
      inventorySnapshot.forEach(doc => {
        inventoryData.push({ id: doc.id, ...doc.data() });
      });
      setInventoryItems(inventoryData);

      processInvoices();
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
  };

  const processInvoices = () => {
    try {
      // Collect all unique items across filteredInvoices
      const uniqueItems = new Set();
      filteredInvoices.forEach((invoice) =>
        invoice.items?.forEach((item) => uniqueItems.add(item.title))
      );

      const itemList = Array.from(uniqueItems).map((title) => ({ title }));

      // Get unique restaurant names
      const restaurantNames = [...new Set(filteredInvoices.map(invoice => invoice.restaurantName).filter(Boolean))];

      // Generate restaurant-wise item quantities
      const restaurantGrid = restaurantNames.map((restaurantName) => {
        const orders = filteredInvoices.filter(
          (invoice) => invoice.restaurantName === restaurantName
        );

        const itemQuantities = itemList.map(item => {
          let quantity = 0;
          let actualQuantity = null;
          let actualQuantityUnit = null;

          orders.forEach(order => {
            order.items?.forEach(orderItem => {
              if (orderItem.title === item.title) {
                quantity += orderItem.quantity || 0;

                // Lookup inventory item by refId
                const inventoryItem = inventoryItemsMap[orderItem.refId];
                if (inventoryItem && inventoryItem.oneUnitQuantity > 0) {
                  actualQuantity = (actualQuantity || 0) + (orderItem.quantity * inventoryItem.oneUnitQuantity);
                  actualQuantityUnit = inventoryItem.oneUnitQuantityUnit || inventoryItem.units;
                }
              }
            });
          });

          return { title: item.title, quantity, actualQuantity, actualQuantityUnit, refId: orders[0]?.items.find(i => i.title === item.title)?.refId };
        });

        return { restaurantName, itemQuantities };
      });

      // Categorize items
      const categorizedItems = {};

      filteredInvoices.forEach((invoice) => {
        invoice.items?.forEach((item) => {
          const categoryName = categories[item.categoryId] || "Uncategorized";

          if (!categorizedItems[categoryName]) {
            categorizedItems[categoryName] = [];
          }

          if (!categorizedItems[categoryName].some((i) => i.title === item.title)) {
            categorizedItems[categoryName].push(item);
          }
        });
      });

      const sortedCategories = Object.keys(categorizedItems).sort();

      const sortedCategorizedItems = {};
      sortedCategories.forEach(category => {
        sortedCategorizedItems[category] = categorizedItems[category].sort((a, b) =>
          a.title.localeCompare(b.title)
        );
      });

      setItems(sortedCategorizedItems);
      setData(restaurantGrid);
    } catch (error) {
      console.error("Error processing invoices: ", error);
    }
  };

  const convertToMainUnit = (quantity, unit) => {
    const conversionMap = {
      kg: { subunits: ["g", "gram", "grams"], factor: 1000 },
      litre: { subunits: ["ml", "milliliter", "milliliters"], factor: 1000 },
    };

    for (const mainUnit in conversionMap) {
      const { subunits, factor } = conversionMap[mainUnit];
      if (subunits.includes(unit?.toLowerCase()) && quantity >= factor) {
        return {
          quantity: quantity / factor,
          unit: mainUnit,
        };
      }
    }
    return { quantity, unit };
  };

  const downloadExcelWithActualTotals = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    const headers = ["Item"];
    data.forEach((restaurant) => {
      headers.push(restaurant.restaurantName);
      headers.push(`Actual Totals`);
    });
    headers.push("Total");
    headers.push("Grand Actual Total");

    worksheetData.push(headers);

    const sortedCategories = Object.keys(items).sort();

    sortedCategories.forEach((category) => {
      worksheetData.push([category, ...Array(data.length * 2 + 1).fill("")]);

      const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

      sortedCategoryItems.forEach((item) => {
        const row = [item.title];
        let rowTotal = 0;
        let rowGrandActualTotal = 0;

        data.forEach((restaurant) => {
          const foundItem = restaurant.itemQuantities.find(q => q.title === item.title) || {};
          const quantity = foundItem.quantity || 0;
          row.push(quantity);
          rowTotal += quantity;

          const invItem = inventoryItems.find(inv => inv.id === item.refId);
          let actualTotal = 0;
          if (invItem && invItem.oneUnitQuantity > 0) {
            actualTotal = quantity * invItem.oneUnitQuantity;
            rowGrandActualTotal += actualTotal;
          }

          const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
          const converted = convertToMainUnit(actualTotal, unit);
          row.push(`${converted.quantity} ${converted.unit}`);
        });

        row.push(rowTotal);

        const invItem = inventoryItems.find(inv => inv.id === item.refId);
        const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
        const convertedGrand = convertToMainUnit(rowGrandActualTotal, unit);
        row.push(`${convertedGrand.quantity} ${convertedGrand.unit}`);

        worksheetData.push(row);
      });
    });

    const totalRow = ["Total"];
    let grandTotal = 0;
    const restaurantActualTotals = [];

    data.forEach((restaurant) => {
      const restaurantTotal = restaurant.itemQuantities.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      totalRow.push(restaurantTotal);
      grandTotal += restaurantTotal;

      let restaurantActualTotal = 0;
      restaurant.itemQuantities.forEach(item => {
        const invItem = inventoryItems.find(inv => inv.title === item.title || inv.id === item.refId);
        if (invItem && invItem.oneUnitQuantity > 0) {
          restaurantActualTotal += item.quantity * invItem.oneUnitQuantity;
        }
      });
      restaurantActualTotals.push(restaurantActualTotal);
    });

    data.forEach((restaurant, index) => {
      const invItem = inventoryItems.find(inv => inv.id === restaurant.itemQuantities[0]?.refId);
      const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
      const converted = convertToMainUnit(restaurantActualTotals[index], unit);
      totalRow.splice(totalRow.length - 1, 0, `${converted.quantity} ${converted.unit}`);
    });

    totalRow.push(grandTotal);

    const grandActualTotal = restaurantActualTotals.reduce((sum, val) => sum + val, 0);
    const invItem = inventoryItems.find(inv => inv.id !== undefined);
    const unit = invItem?.oneUnitQuantityUnit || invItem?.units;
    const convertedGrandTotal = convertToMainUnit(grandActualTotal, unit);
    totalRow.push(`${convertedGrandTotal.quantity} ${convertedGrandTotal.unit}`);

    worksheetData.push(totalRow);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const colWidths = [];
    headers.forEach(() => colWidths.push({ wch: 18 }));
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "Detailed Orders");
    XLSX.writeFile(workbook, `orders_detailed_${dateFilter}.xlsx`);
  };

  const downloadPDFByRestaurant = () => {
    try {
      if (typeof jsPDF === 'undefined') {
        alert('PDF generation is not available. Please try again.');
        return;
      }

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      let isFirstPage = true;

      data.forEach((restaurant, restaurantIndex) => {
        if (!isFirstPage) {
          doc.addPage();
        }
        isFirstPage = false;

        doc.setFontSize(18);
        doc.text(`${restaurant.restaurantName}`, 15, 15);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 15, 22);

        const headers = ["Item", "Quantity", "Actual Total"];
        const rows = [];
        const sortedCategories = Object.keys(items).sort();
        let restaurantGrandTotal = 0;
        let restaurantGrandActualTotal = 0;

        sortedCategories.forEach((category) => {
          const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

          sortedCategoryItems.forEach((item) => {
            const foundItem = restaurant.itemQuantities.find(q => q.title === item.title) || {};
            const quantity = foundItem.quantity || 0;

            if (quantity > 0) {
              restaurantGrandTotal += quantity;

              const invItem = inventoryItems.find(inv => inv.id === item.refId);
              let actualTotal = 0;
              let actualTotalStr = "-";

              if (invItem && invItem.oneUnitQuantity > 0) {
                actualTotal = quantity * invItem.oneUnitQuantity;
                restaurantGrandActualTotal += actualTotal;
                const unit = invItem.oneUnitQuantityUnit || invItem.units;
                const converted = convertToMainUnit(actualTotal, unit);
                actualTotalStr = `${converted.quantity} ${converted.unit}`;
              }

              rows.push({
                content: [item.title, quantity.toString(), actualTotalStr],
                isCategory: false,
                isGrandTotal: false
              });
            }
          });
        });

        const invItem = inventoryItems[0];
        const unit = invItem?.oneUnitQuantityUnit || invItem?.units || "";
        const convertedGrandTotal = convertToMainUnit(restaurantGrandActualTotal, unit);

        rows.push({
          content: ["TOTAL", restaurantGrandTotal.toString(), `${convertedGrandTotal.quantity} ${convertedGrandTotal.unit}`],
          isGrandTotal: true
        });

        doc.autoTable({
          head: [headers],
          body: rows.map(row => row.content),
          startY: 28,
          theme: 'grid',
          styles: {
            fontSize: 11,
            cellPadding: 3,
            font: 'helvetica',
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
          },
          headStyles: {
            fillColor: [200, 200, 200],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 11,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          bodyStyles: {
            font: 'helvetica',
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
            lineColor: [0, 0, 0],
            lineWidth: 0.3,
          },
          willDrawCell: (cellData) => {
            if (cellData && cellData.row && typeof cellData.row.index !== 'undefined') {
              const rowIndex = cellData.row.index;
              const rowInfo = rows[rowIndex];

              if (rowInfo && rowInfo.isGrandTotal) {
                doc.setFillColor(220, 220, 220);
                doc.rect(cellData.cell.x, cellData.cell.y, cellData.cell.width, cellData.cell.height, 'F');
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(11);
                doc.setFont("helvetica", "bold");
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.5);
                doc.rect(cellData.cell.x, cellData.cell.y, cellData.cell.width, cellData.cell.height);
              }
            }
          }
        });
      });

      doc.save(`orders_by_restaurant_${dateFilter}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
    }
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    const headers = ["Item", ...data.map((d) => d.restaurantName), "Total", "Actual Total Qty"];
    worksheetData.push(headers);

    const sortedCategories = Object.keys(items).sort();

    sortedCategories.forEach((category) => {
      worksheetData.push([category, ...Array(data.length + 2).fill("")]);

      const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

      sortedCategoryItems.forEach((item) => {
        const row = [item.title];
        let rowTotal = 0;
        let rowActualTotal = 0;

        data.forEach((restaurant) => {
          const foundItem = restaurant.itemQuantities.find(q => q.title === item.title) || {};
          const quantity = foundItem.quantity || 0;
          row.push(quantity);
          rowTotal += quantity;

          const invItem = inventoryItems.find(inv => inv.id === item.refId);
          if (invItem && invItem.oneUnitQuantity > 0) {
            rowActualTotal += quantity * invItem.oneUnitQuantity;
          }
        });

        row.push(rowTotal);
        if (rowActualTotal && item.refId) {
          const inventoryItem = inventoryItems.find(inv => inv.id === item.refId);
          const unit = inventoryItem?.oneUnitQuantityUnit || inventoryItem?.units;
          const converted = convertToMainUnit(rowActualTotal, unit);
          row.push(`${converted.quantity} ${converted.unit}`);
        } else {
          row.push("-");
        }

        worksheetData.push(row);
      });
    });

    const totalRow = ["Total"];
    let grandTotal = 0;

    data.forEach((restaurant) => {
      const restaurantTotal = restaurant.itemQuantities.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
      totalRow.push(restaurantTotal);
      grandTotal += restaurantTotal;
    });

    totalRow.push(grandTotal);
    worksheetData.push(totalRow);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(workbook, `orders_${dateFilter}.xlsx`);
  };

  const downloadPDF = () => {
    try {
      if (typeof jsPDF === 'undefined') {
        alert('PDF generation is not available. Please try again.');
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a3",
      });

      const headers = ["Item", ...data.map((d) => d.restaurantName), "Total", "Actual Total Qty"];
      const rows = [];
      const sortedCategories = Object.keys(items).sort();

      sortedCategories.forEach((category) => {
        rows.push({
          content: [category, ...Array(data.length + 2).fill("")],
          isCategory: true
        });

        const sortedCategoryItems = items[category].sort((a, b) => a.title.localeCompare(b.title));

        sortedCategoryItems.forEach((item) => {
          const row = [item.title];
          let rowTotal = 0;
          let rowActualTotal = 0;

          data.forEach((restaurant) => {
            const quantity = restaurant.itemQuantities.find((q) => q.title === item.title)?.quantity || 0;
            row.push(quantity.toString());
            rowTotal += quantity;

            const invItem = inventoryItems.find(inv => inv.id === item.refId);
            if (invItem && invItem.oneUnitQuantity > 0) {
              rowActualTotal += quantity * invItem.oneUnitQuantity;
            }
          });

          row.push(rowTotal.toString());

          if (item.refId) {
            const inventoryItem = inventoryItems.find(inv => inv.id === item.refId);
            const unit = inventoryItem?.oneUnitQuantityUnit || inventoryItem?.units;
            const converted = convertToMainUnit(rowActualTotal, unit);
            row.push(`${converted.quantity} ${converted.unit}`);
          } else {
            row.push("-");
          }

          rows.push({
            content: row,
            isCategory: false
          });
        });
      });

      doc.setFontSize(26);
      doc.text("Orders Grid Summary", 170, 15);

      doc.autoTable({
        head: [headers],
        body: rows.map(row => row.content),
        startY: 20,
        theme: 'grid',
        styles: {
          fontSize: 13,
          cellPadding: 3,
          font: 'helvetica',
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
        },
        headStyles: {
          fillColor: [200, 200, 200],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 12,
          font: 'helvetica',
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
        },
        bodyStyles: {
          font: 'helvetica',
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
        },
        willDrawCell: (cellData) => {
          if (cellData && cellData.row && typeof cellData.row.index !== 'undefined') {
            const rowIndex = cellData.row.index;
            const isCategory = rows[rowIndex] && rows[rowIndex].isCategory;

            if (isCategory) {
              doc.setFillColor(180, 180, 180);
              doc.rect(cellData.cell.x, cellData.cell.y, cellData.cell.width, cellData.cell.height, 'F');

              doc.setTextColor(0, 0, 0);
              doc.setFontSize(14);
              doc.setFont("helvetica", "bold");

              doc.text(
                cellData.cell.raw ? String(cellData.cell.raw) : "",
                cellData.cell.x + 4,
                cellData.cell.y + cellData.cell.height / 2 + 2
              );

              doc.setDrawColor(0, 0, 0);
              doc.setLineWidth(0.5);
              doc.rect(cellData.cell.x, cellData.cell.y, cellData.cell.width, cellData.cell.height);
            }
          }
        }
      });

      doc.save(`orders_${dateFilter}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF: ' + error.message);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
        
        {/* Title and Top Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" color="text.primary" sx={{ fontWeight: "bold", mb: 1 }}>
            Order Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View combined restaurant orders, apply history filters, and generate combined/restaurant reports.
          </Typography>
        </Box>

        {/* Date Filters Control Card */}
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="date-filter-label">Select Period</InputLabel>
                <Select
                  labelId="date-filter-label"
                  value={dateFilter}
                  label="Select Period"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="Today">Today</MenuItem>
                  <MenuItem value="Yesterday">Yesterday</MenuItem>
                  <MenuItem value="Last 3 Days">Last 3 Days</MenuItem>
                  <MenuItem value="Custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {dateFilter === "Custom" && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* Loading Indicator */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress color="primary" />
          </Box>
        ) : filteredInvoices.length === 0 ? (
          <Box p={2}>
            <Alert severity="warning" icon={<WarningIcon />}>
              <AlertTitle>No Orders Found</AlertTitle>
              There are no orders found for the selected period ({dateFilter}).
            </Alert>
          </Box>
        ) : (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            
            {/* Table Header Details */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" color="text.primary" sx={{ fontWeight: "bold" }}>
                Orders Grid By Restaurant
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {getRangeText()}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRefresh}
                startIcon={<RefreshIcon />}
                sx={{ borderRadius: 2, fontWeight: 'bold' }}
              >
                Refresh
              </Button>
            </Box>

            {/* Table Wrapper for Horizontal Scroll */}
            <Box
              sx={{
                width: "100%",
                overflowX: "auto",
                position: "relative",
                mb: 4,
                "&::-webkit-scrollbar": { height: "8px" },
                "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: "4px" },
              }}
              onScroll={handleScroll}
            >
              <table
                style={{
                  minWidth: "100%",
                  width: "max-content",
                  borderCollapse: "collapse",
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: "#e8f5e9",
                        color: "#2E7D32",
                        padding: "16px",
                        border: "1px solid #ffffff",
                        minWidth: "180px",
                        maxWidth: "250px",
                      }}
                    >
                      Item
                    </th>
                    {data.map((restaurant, index) => (
                      <th
                        key={index}
                        style={{
                          backgroundColor: "#e8f5e9",
                          color: "#2E7D32",
                          padding: "16px",
                          border: "1px solid #ffffff",
                          minWidth: "150px",
                        }}
                      >
                        {restaurant.restaurantName}
                      </th>
                    ))}
                    <th
                      style={{
                        backgroundColor: "#e8f5e9",
                        color: "#2E7D32",
                        padding: "16px",
                        border: "1px solid #ffffff",
                        minWidth: "80px",
                        position: "sticky",
                        right: 80,
                        zIndex: 3,
                      }}
                    >
                      Total
                    </th>
                    <th
                      style={{
                        backgroundColor: "#e8f5e9",
                        color: "#2E7D32",
                        padding: "16px",
                        border: "1px solid #ffffff",
                        minWidth: "120px",
                        position: "sticky",
                        right: 0,
                        zIndex: 3,
                      }}
                    >
                      Actual Total quantity
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(items).map(([category, categoryItems], catIndex) => (
                    <React.Fragment key={catIndex}>
                      <tr>
                        <td
                          colSpan={data.length + 3}
                          style={{
                            position: "relative",
                            padding: 0,
                            height: "40px",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          <div
                            className="category-label"
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              zIndex: 4,
                              backgroundColor: "#f8f9fa",
                              fontWeight: "bold",
                              textAlign: "left",
                              padding: "10px 16px",
                              border: "1px solid #ddd",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              color: "#2E7D32",
                            }}
                          >
                            {category}
                          </div>
                        </td>
                      </tr>

                      {categoryItems.map((item, index) => {
                        const current = inventoryItems.find((inventoryItem) => inventoryItem.id === item.refId);

                        const total = data.reduce((sum, restaurant) => {
                          const quantity = restaurant.itemQuantities.find(
                            (q) => q.title === item.title
                          )?.quantity || 0;
                          return sum + quantity;
                        }, 0);

                        return (
                          <tr key={index}>
                            <td
                              style={{
                                padding: "16px",
                                border: "1px solid #ddd",
                                fontWeight: "500",
                                position: "sticky",
                                left: 0,
                                backgroundColor: "#fff",
                                zIndex: 2,
                              }}
                            >
                              {item.title}
                            </td>

                            {data.map((restaurant, i) => (
                              <td
                                key={i}
                                style={{
                                  padding: "16px",
                                  border: "1px solid #ddd",
                                  textAlign: "center",
                                }}
                              >
                                {restaurant.itemQuantities.find(
                                  (q) => q.title === item.title
                                )?.quantity || "-"}
                              </td>
                            ))}

                            <td
                              style={{
                                padding: "16px",
                                border: "1px solid #ddd",
                                fontWeight: "bold",
                                textAlign: "center",
                                position: "sticky",
                                right: 80,
                                backgroundColor: "#fff",
                                zIndex: 2,
                              }}
                            >
                              {total}
                            </td>
                            <td
                              style={{
                                padding: "16px",
                                border: "1px solid #ddd",
                                fontWeight: "bold",
                                textAlign: "center",
                                position: "sticky",
                                right: 0,
                                backgroundColor: "#fff",
                                zIndex: 2,
                              }}
                            >
                              {current && current.oneUnitQuantity > 0
                                ? (() => {
                                  const converted = convertToMainUnit(current.oneUnitQuantity * total, current.oneUnitQuantityUnit || current.units);
                                  return `${converted.quantity} ${converted.unit}`;
                                })()
                                : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}

                  {/* Grand Total Row */}
                  <tr style={{ backgroundColor: "#f1f1f1", fontWeight: "bold" }}>
                    <td
                      style={{
                        padding: "16px",
                        border: "1px solid #ddd",
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#f1f1f1",
                        zIndex: 2,
                      }}
                    >
                      Total
                    </td>

                    {data.map((restaurant, i) => {
                      const grandTotal = restaurant.itemQuantities.reduce(
                        (sum, item) => sum + item.quantity,
                        0
                      );
                      return (
                        <td
                          key={i}
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            textAlign: "center",
                            backgroundColor: "#f1f1f1",
                          }}
                        >
                          {grandTotal}
                        </td>
                      );
                    })}

                    <td
                      style={{
                        padding: "16px",
                        border: "1px solid #ddd",
                        fontWeight: "bold",
                        textAlign: "center",
                        position: "sticky",
                        right: 80,
                        backgroundColor: "#f1f1f1",
                        zIndex: 2,
                      }}
                    >
                      {data.reduce(
                        (sum, restaurant) =>
                          sum +
                          restaurant.itemQuantities.reduce(
                            (s, i) => s + i.quantity,
                            0
                          ),
                        0
                      )}
                    </td>
                    
                    <td
                      style={{
                        padding: "16px",
                        border: "1px solid #ddd",
                        backgroundColor: "#f1f1f1",
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                      }}
                    >
                      -
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>

            {/* Action Buttons for Download Options */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={downloadPDF}
                sx={{ minWidth: 160, backgroundColor: "#D32F2F", '&:hover': { backgroundColor: '#B71C1C' } }}
                startIcon={<PictureAsPdfIcon />}
              >
                Download PDF (Combined)
              </Button>
              <Button
                variant="contained"
                onClick={downloadPDFByRestaurant}
                sx={{ minWidth: 160, backgroundColor: "#E65100", '&:hover': { backgroundColor: '#E65100' } }}
                startIcon={<PictureAsPdfIcon />}
              >
                Download PDF (By Restaurant)
              </Button>
              <Button
                variant="contained"
                onClick={downloadExcel}
                sx={{ minWidth: 160, backgroundColor: "#2E7D32", '&:hover': { backgroundColor: '#1B5E20' } }}
                startIcon={<TableChartIcon />}
              >
                Download Excel
              </Button>
              <Button
                variant="contained"
                onClick={downloadExcelWithActualTotals}
                sx={{ minWidth: 160, backgroundColor: "#0277BD", '&:hover': { backgroundColor: '#01579B' } }}
                startIcon={<TableChartIcon />}
              >
                Download Excel (Detailed)
              </Button>
            </Box>

          </Paper>
        )}

      </Box>
    </ThemeProvider>
  );
};

export default OrderDashboard;
